import React, { ChangeEvent, useState, ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import { Button, makeStyles, Typography, Box } from '@material-ui/core';
import { addDays, format, isSameDay, isWithinInterval, isValid, startOfDay } from 'date-fns';
import { TFunction } from 'i18next';
import { ModalContainer, ModalContent, ModalFooter, ModalHeader } from 'components/@common/Modal';
import PageContainer from 'components/@layout/PageContainer';
import BULK_ASSIGN_AVAILABLE_LICENSES from 'graph/license/bulk-assign-available-licenses-mutation';
import GET_MASTERCODE_ACTIVATION_DETAILS from 'graph/orders/get-mastercode-activation-details-query';
import SCHEDULE_UNLOCK_MASTERCODE from 'graph/orders/schedule-unlock-mastercode-mutation';
import SCHEDULE_ORDER_UNLOCK_MASTERCODE from 'graph/orders/schedule-unlock-order-mastercode-mutation';
import UNLOCK_MASTERCODE from 'graph/orders/unlock-mastercode-mutation';
import UNLOCK_ORDER_MASTERCODE from 'graph/orders/unlock-order-mastercode-mutation';
import useStore from 'store';
import { VoidCallback, Theme, AlertOptions } from 'types';
import { ActivationRangeOptions, AlertVariant } from 'types/enums';
import {
  Mastercode,
  Order,
  OrderItemProvisioningResult,
  ProductType,
  BulkAssignAvailableLicenseInput
} from 'types/schema-generated-types';
import getOrderNumber from 'utilities/get-order-number';
import {
  getBulkAssignAvailableCacheUpdate,
  getMyOrdersMutationCacheUpdate
} from '../../utils/cacheUpdater';
import { ORDER_DATE_FORMAT } from '../../utils/constants';
import MastercodeModalDetails from '../components/MastercodeModalDetails';
import ActivationAlertDetails from './components/ActivationAlertDetails';
import ActivateMastercodeForm from './components/ActivateMastercodeForm';
import ActivationWarningText from './components/ActivationWarningText';
import MastercodeExpirationList from './components/MastercodeExpirationList';

export type Props = {
  activateAll?: boolean;
  mastercodeId?: number;
  order: Order;
  onClose: VoidCallback;
  onRosterManagementClick: VoidCallback;
  onSubmit?: VoidCallback;
};

export interface MastercodeExpirationDate {
  code: string;
  date: Date;
}

const getSuccessAlert = (t: TFunction, html: ReactElement): AlertOptions => ({
  variant: AlertVariant.SUCCESS,
  title: t('mastercodeActivated'),
  html,
  showCancelButton: false,
  showCloseButton: true,
  confirmButtonText: t('rosterSetupButton')
});

const getWarningAlert = (t: TFunction, html: ReactElement): AlertOptions => ({
  variant: AlertVariant.WARNING,
  title: t('confirmMastercodeActivation'),
  html,
  confirmButtonText: t('Activate')
});

export const hasSameExpirationDate = (
  expirationDates: MastercodeExpirationDate[] = []
): boolean => {
  if (expirationDates.length <= 1) return true;

  const [firstExpirationDate, ...remainingExpirationDates] = expirationDates;
  return remainingExpirationDates.every(({ date }) => isSameDay(date, firstExpirationDate.date));
};

const ActivateMastercodeModal = ({
  activateAll = false,
  order,
  mastercodeId,
  onClose,
  onRosterManagementClick,
  onSubmit: onSubmitHandlerProp
}: Props) => {
  const classes = useStyles();
  const [{ user }, { closeModal, openAlert }] = useStore();
  const { t } = useTranslation('Order');

  const now = new Date();
  const minDate = addDays(startOfDay(now), 1);
  const maxDate = addDays(startOfDay(now), 90);

  const [activationDate, setActivationDate] = useState<Date | null>(minDate);
  const [activationRangeDate, setActivationRangeDate] = useState(ActivationRangeOptions.NOW);

  const { data: activationDetails, loading: mastercodeActivationDetailsLoading } = useQuery(
    GET_MASTERCODE_ACTIVATION_DETAILS,
    {
      variables: {
        where: { orders: { id: order.id }, unlocked: false },
        expirationWhere: { dateUnlocked: activationDate }
      },
      skip: !activationDate
    }
  );

  const mastercodesToActivate: Mastercode[] = [];
  const expirationDates: MastercodeExpirationDate[] = [];
  for (const mastercode of activationDetails?.mastercodes ?? []) {
    if (mastercode.dateUnlocked) continue;
    if (mastercodeId && mastercodeId !== mastercode.id) continue;

    const orderItem = mastercode.orderItems?.[0];
    if (orderItem?.dateExpiration) {
      expirationDates.push({ code: mastercode.code, date: new Date(orderItem.dateExpiration) });
    }

    mastercodesToActivate.push(mastercode);
  }

  const [unlockMastercode, { loading: unlockMastercodeLoading }] = useMutation(
    UNLOCK_MASTERCODE,
    getMyOrdersMutationCacheUpdate(user.me?.id)
  );

  const [scheduleUnlockMastercode, { loading: scheduleUnlockMastercodeLoading }] = useMutation(
    SCHEDULE_UNLOCK_MASTERCODE,
    getMyOrdersMutationCacheUpdate(user.me?.id)
  );

  const [unlockOrderMastercodes, { loading: unlockOrderMastercodesLoading }] = useMutation(
    UNLOCK_ORDER_MASTERCODE,
    getMyOrdersMutationCacheUpdate(user.me?.id)
  );

  const [
    scheduleUnlockOrderMastercodes,
    { loading: scheduleUnlockOrderMastercodesLoading }
  ] = useMutation(SCHEDULE_ORDER_UNLOCK_MASTERCODE, getMyOrdersMutationCacheUpdate(user.me?.id));

  const [
    bulkAssignAvailableLicenses,
    { loading: bulkAssignAvailableLicensesLoading }
  ] = useMutation(BULK_ASSIGN_AVAILABLE_LICENSES, getBulkAssignAvailableCacheUpdate(user.me?.id));

  const handleDateActivationChange = (date: Date | null) => setActivationDate(date);

  const doMastercodeUnlock = async (mastercode: Mastercode) => {
    const { data } = await unlockMastercode({ variables: { mastercodeId: mastercode.id } });

    const unlockResult = data.unlockMastercode;
    if (unlockResult?.success) await assignLicenses(unlockResult.success);
  };

  const doOrderUnlock = async (order: Order) => {
    const { data } = await unlockOrderMastercodes({ variables: { orderId: order.id } });

    const unlockResult = data.unlockOrderMastercodes;
    if (unlockResult?.success) await assignLicenses(unlockResult.success);
  };

  const assignLicenses = async (orderItemProvisioningResults: OrderItemProvisioningResult[]) => {
    const assignRequests = orderItemProvisioningResults.reduce(
      (acc, orderItemProvisioningResult) => {
        const { orderItem } = orderItemProvisioningResult;
        if (orderItem?.product?.type === ProductType.USER) {
          const request = {
            userId: user.me?.id as number,
            orderItemId: [orderItem.id],
            action: 'ASSIGN',
            skipAlreadyLicensed: true
          };
          acc.push(request);
        }
        return acc;
      },
      [] as BulkAssignAvailableLicenseInput[]
    );

    if (assignRequests.length > 0) {
      await bulkAssignAvailableLicenses({ variables: { assignRequests } });
    }
  };

  const handleActivationChange = (event: ChangeEvent<HTMLInputElement>) =>
    setActivationRangeDate(event.target.value as ActivationRangeOptions);

  const AlertDetails =
    expirationDates.length > 0 && hasSameExpirationDate(expirationDates) ? (
      t('expireDate', { date: format(expirationDates[0].date, ORDER_DATE_FORMAT) })
    ) : (
      <MastercodeExpirationList expirationDates={expirationDates} className={classes.alertList} />
    );

  const ActivationConfirmDetails = (
    <ActivationAlertDetails
      order={order}
      activationDate={activationDate}
      className={classes.alertContainer}
    >
      {AlertDetails}
    </ActivationAlertDetails>
  );

  const ActivationSuccessDetails = (
    <ActivationAlertDetails order={order} activationDate={activationDate}>
      {AlertDetails}
      <Box marginTop={3}>{t('rosterSetup')}</Box>
    </ActivationAlertDetails>
  );

  const handleOnRosterManagementClick = () => {
    closeModal();
    onRosterManagementClick();
  };

  const handleOnConfirmMastercodeModal = async (mastercode: Mastercode) => {
    try {
      if (activationRangeDate === ActivationRangeOptions.LATER) {
        await scheduleUnlockMastercode({
          variables: { mastercodeId: mastercode.id, date: activationDate }
        });
      } else {
        await doMastercodeUnlock(mastercode);
      }
      openAlert({
        ...getSuccessAlert(t, ActivationSuccessDetails),
        onSubmit: handleOnRosterManagementClick
      });
    } catch (error) {
      //TODO: Handle Error
      console.log('Error', error);
    }
  };

  const handleOnConfirmOrderModal = async (order: Order) => {
    try {
      if (activationRangeDate === ActivationRangeOptions.LATER) {
        await scheduleUnlockOrderMastercodes({
          variables: { orderId: order.id, date: activationDate }
        });
      } else {
        await doOrderUnlock(order);
      }

      openAlert({
        ...getSuccessAlert(t, ActivationSuccessDetails),
        onSubmit: handleOnRosterManagementClick
      });
    } catch (error) {
      //TODO: Handle Error
      console.log('Error', error);
    }
  };

  const submitForm = async () => {
    openAlert({
      ...getWarningAlert(t, ActivationConfirmDetails),
      onSubmit: async result => {
        if (!result.value) return;

        if (activateAll) {
          await handleOnConfirmOrderModal(order);
        }

        const mastercode = mastercodesToActivate.find(m => m.id === mastercodeId);
        if (mastercode) {
          await handleOnConfirmMastercodeModal(mastercode);
        }

        if (onSubmitHandlerProp) await onSubmitHandlerProp();

        closeModal();
      }
    });
  };

  const isLoading =
    bulkAssignAvailableLicensesLoading ||
    scheduleUnlockMastercodeLoading ||
    scheduleUnlockOrderMastercodesLoading ||
    unlockMastercodeLoading ||
    unlockOrderMastercodesLoading ||
    mastercodeActivationDetailsLoading;

  const isValidDate = useMemo(() => {
    if (activationRangeDate !== ActivationRangeOptions.LATER) return true;
    if (!activationDate || !isValid(activationDate)) return false;

    return isWithinInterval(activationDate, { start: minDate, end: maxDate });
  }, [activationRangeDate, activationDate, maxDate, minDate]);

  return (
    <PageContainer showChildrenWhileLoading loading={isLoading}>
      <ModalContainer
        Header={
          <ModalHeader>
            <Typography noWrap variant="h2" component="h1">
              {t('orderNumber', { orderNumber: getOrderNumber(order) })}
            </Typography>
          </ModalHeader>
        }
        Footer={
          <ModalFooter
            Actions={
              <>
                <Button
                  aria-label={t('Cancel')}
                  color="secondary"
                  variant="outlined"
                  disableElevation
                  onClick={onClose}
                >
                  {t('Cancel')}
                </Button>
                <Button
                  aria-label={t('Confirm & Activate')}
                  variant="contained"
                  color="primary"
                  disabled={!isValidDate || isLoading}
                  disableElevation
                  onClick={submitForm}
                >
                  {t('Confirm & Activate')}
                </Button>
              </>
            }
          />
        }
      >
        <ModalContent className={classes.body}>
          <MastercodeModalDetails
            isActivateCode
            order={order}
            mastercodes={mastercodesToActivate}
          />
          <ActivateMastercodeForm
            nowDate={now}
            minDate={minDate}
            maxDate={maxDate}
            isValidDate={isValidDate}
            warningText={
              <ActivationWarningText
                activationDate={activationDate}
                expirationDates={expirationDates}
              />
            }
            activationRangeValue={activationRangeDate}
            activationRangeDate={activationDate}
            handleActivationChange={handleActivationChange}
            handleDateActivationChange={handleDateActivationChange}
          />
        </ModalContent>
      </ModalContainer>
    </PageContainer>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  body: {
    padding: theme.spacing(1)
  },
  alertContainer: {
    paddingBottom: theme.spacing(1.5)
  },
  alertList: {
    display: 'inline-block',
    padding: '0 15px',
    textAlign: 'left'
  }
}));

export default ActivateMastercodeModal;
